import type { TFunction } from 'i18next';
import { boolean, object } from 'yup';

import {
  makeAgreeToTerms,
  makeCustomDescription,
  makeEmail,
  makeEnableSMS,
  makeInviteEmail,
  makeName,
  makeOrg,
  makeOrgDescriptions,
  makeOrgName,
  makeOrgUsageMax,
  makeOrgUsageMin,
  makePersona,
} from 'src/modules/onboarding/validations/validations';
import type { CheckEmailAuthenticationMethod } from 'src/shared/hooks/useAuthenticationMethod';
import { makePassword } from 'src/shared/validationSchemas';
import { make as makePhoneNumber } from 'src/shared/validationSchemas/phoneNumber';

export const makeNameValidationSchema = (t: TFunction) =>
  object().shape({
    firstName: makeName(t),
  });

export const makePersonaValidationSchema = () =>
  object().shape({
    personaType: makePersona(),
  });

export const makeOrgValidationSchema = () =>
  object().shape({
    orgType: makeOrg(),
  });

export const makeEmailValidationSchema = (t: TFunction) =>
  object().shape({
    email: makeEmail(t),
  });

export const makeSingleSignOnValidationSchema = (
  t: TFunction,
  checkEmail: CheckEmailAuthenticationMethod | null,
) =>
  object().shape({
    email: makeEmail(t).test(
      'isSingleSignOnEnabled',
      t('onboarding.thisEmailIsNotSSOEnabled'),
      async email => {
        if (!makeEmail(t).isValidSync(email) || !checkEmail) return false;

        return checkEmail(email, { beacon: false, singleSignOn: true });
      },
    ),
  });

export const makeOrgDescriptionValidationSchema = () =>
  object().shape({
    orgDescriptions: makeOrgDescriptions(),
    customDescription: makeCustomDescription(),
  });

export const makeOrgUsageValidationSchema = () =>
  object().shape({
    orgUsage: object().shape({
      min: makeOrgUsageMin(),
      max: makeOrgUsageMax(),
    }),
  });

export const makeOrgNameValidationSchema = (t: TFunction) =>
  object().shape({
    orgName: makeOrgName(t),
  });

export const makeDownloadAppValidationSchema = () =>
  object().shape({
    enableSMS: makeEnableSMS(),
  });

export const makePhoneNumberValidationSchema = (t: TFunction) =>
  object().shape({
    phoneNumber: makePhoneNumber(
      t,
      'onboarding.phoneNumberIsRequired',
      'onboarding.pleaseProvideAValidPhoneNumber',
    ),
    enableSMS: boolean().required(),
  });

export const makePasswordValidationSchema = (t: TFunction) =>
  object().shape({
    email: makeEmail(t),
    password: makePassword(t),
  });

export const makeInviteValidationSchema = (t: TFunction) =>
  object().shape({
    firstEmail: makeInviteEmail(t),
    secondEmail: makeInviteEmail(t),
    thirdEmail: makeInviteEmail(t),
  });

export const makeSignupValidationSchema = (t: TFunction) =>
  object().shape({
    email: makeEmail(t),
    agreeToTerms: makeAgreeToTerms(t),
  });
